import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PageService from '../services/pageService/PageService';

const HeaderFooterContext = createContext();

export const useHeaderFooter = () => {
  const context = useContext(HeaderFooterContext);
  if (!context) {
    throw new Error('useHeaderFooter must be used within a HeaderFooterProvider');
  }
  return context;
};

export const HeaderFooterProvider = ({ children }) => {
  const [headerElement, setHeaderElement] = useState(null);
  const [footerElement, setFooterElement] = useState(null);
  const [socialMediaLinks, setSocialMediaLinks] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [copyright, setCopyright] = useState(null);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const [previousRouteType, setPreviousRouteType] = useState(null);

  const fetchHeaderFooterData = async () => {
    try {
      setLoading(true);
      const response = await PageService.GetHeaderAndFooter();
      const res = response.data;
      
      if (res && res.status && res.object) {
        setHeaderElement(res.object.headerElement || null);
        setFooterElement(res.object.footerElement || null);
        setSocialMediaLinks(Array.isArray(res.object.socialMediaLinks) ? res.object.socialMediaLinks : []);
        setAddresses(Array.isArray(res.object.addresses) ? res.object.addresses : []);
        setCopyright(res.object.copyright || null);
        setMenus(Array.isArray(res.object.menus) ? res.object.menus : []);
      }
    } catch (error) {
      console.error('Failed to load header/footer data', error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };


  // Function to determine route type
  const getRouteType = (pathname) => {
    const parts = pathname.split('/').filter(part => part);
    
    // Base URL (/)
    if (parts.length === 0) {
      return 'base';
    }
    
    // Custom page route (/page/...)
    if (parts.length >= 2 && parts[0] === 'page') {
      return 'page';
    }
    
    // Division route (/Division/BCS) or any division sub-route
    if (parts.length >= 2 && parts[0] === 'Division' && parts[2] !== 'Department') {
      return 'division';
    }
    
    // Department route (/Division/BCS/Department/cis) or any department sub-route
    if (parts.length >= 4 && parts[0] === 'Division' && parts[2] === 'Department') {
      return 'department';
    }
    
    // Any other route
    return 'other';
  };

  // Listen for location changes and refresh header/footer only when route type changes
  useEffect(() => {
    const currentRouteType = getRouteType(location.pathname);
    
    // Only refresh if route type has changed
    if (previousRouteType === null || previousRouteType !== currentRouteType) {
      // Only refresh for main route types (base, division, department, page)
      if (['base', 'division', 'department', 'page'].includes(currentRouteType)) {
        fetchHeaderFooterData();
      }
      setPreviousRouteType(currentRouteType);
    }
  }, [location.pathname, previousRouteType]);

  const value = {
    headerElement,
    footerElement,
    socialMediaLinks,
    addresses,
    copyright,
    menus,
    loading,
    error,
    refreshHeaderFooter: fetchHeaderFooterData
  };

  return (
    <HeaderFooterContext.Provider value={value}>
      {children}
    </HeaderFooterContext.Provider>
  );
};

export default HeaderFooterContext;
